import { useMemo } from 'react';
import { CACHE } from '../../../engine/api/cache';
import { useResourceList } from '../../../engine/api/hooks';

// The per-tenant catalogs the service-desk screens render from (statuses, priorities,
// outcomes, queues, types, technicians…). Small, long-lived lists: fetched once through the
// engine hooks and cached by TanStack Query; every screen shares the same query keys.

export interface StatusRow { id: string; key: string; name: string; category: 'new' | 'open' | 'pending' | 'resolved' | 'closed' | 'cancelled'; sla_behaviour: 'running' | 'paused' | 'stopped'; is_pending_closure: boolean; colour: string | null; sort: number; is_default: boolean; is_active: boolean }
export interface PriorityRow { id: string; key: string; name: string; level: number; colour: string | null; sort: number; is_default: boolean }
export interface LevelRow { id: string; key: string; name: string; level: number; sort: number; description?: string | null }
export interface OutcomeRow { id: string; key: string; name: string; button_label: string | null; kind: string; icon: string | null; colour: string | null; sort: number; is_active: boolean; status_after_id: string | null; sla_hold: boolean; releases_hold: boolean; is_public: boolean; is_response: boolean; sends_email: boolean; requires_time: boolean; requires_note: boolean; default_minutes: number | null }
export interface QueueRow { id: string; name: string; assignment_mode: string; is_default: boolean; is_escalation: boolean; sort: number }
export interface TypeRow {
  id: string; key: string; name: string; itil_kind: string; icon: string | null; colour: string | null; sort: number; is_default: boolean;
  profile_id?: string | null; priority_mode?: 'matrix' | 'direct' | null;
  field_config: Record<string, Record<string, FieldConfigEntry>>;
  /** The server-resolved view (profile ⊕ type ⊕ priority mode): what every form renders from. */
  effective?: { profile_id: string | null; profile_name: string | null; priority_mode: 'matrix' | 'direct'; field_config: Record<string, Record<string, FieldConfigEntry>> };
}
/** The order ticket form groups read in (the seeded profile rules name these; unknown groups follow). */
export const TICKET_GROUP_ORDER = ['Who', 'What', 'Classification', 'Routing', 'Commercial', 'Links'];
export function orderTicketFields<T extends { group?: string; order?: number }>(fields: T[]): T[] {
  const rank = (g?: string) => { const i = g ? TICKET_GROUP_ORDER.indexOf(g) : -1; return i < 0 ? TICKET_GROUP_ORDER.length : i; };
  return [...fields].sort((a, b) => rank(a.group) - rank(b.group) || (a.order ?? 0) - (b.order ?? 0));
}

/** The field rules a form should apply for a type: the resolved `effective` view, else the raw config. */
export const fieldConfigOf = (type: TypeRow | null | undefined, audience: string): Record<string, FieldConfigEntry> | undefined =>
  (type?.effective?.field_config ?? type?.field_config)?.[audience];
export interface TechnicianRow { id: string; name: string; email: string | null; user_id: string | null; status: string }
export interface FieldCondition { field: string; op: 'eq' | 'ne' | 'empty' | 'not_empty'; value?: string }
export interface FieldConfigEntry {
  visibility: 'hidden' | 'editable' | 'required' | 'readonly';
  label?: string;
  group?: string;
  /** Help text shown under the field. */
  hint?: string;
  /** Shown only when any group's conditions all hold (a connected system's conditional fields). */
  visible_when?: FieldCondition[][];
  /** Options come from a runtime lookup (`GET /v1/ticket-types/:id/field-options`). */
  dynamic?: boolean;
  /** Choosing a value fills other fields (`POST /v1/ticket-types/:id/field-lookup`). */
  database_lookup_id?: number;
  /** The connected system's field id (addresses the lookups). */
  halo_field_id?: number;
}

const holds = (c: FieldCondition, raw: unknown): boolean => {
  const v = raw === null || raw === undefined ? '' : String(raw);
  return c.op === 'eq' ? v === (c.value ?? '') : c.op === 'ne' ? v !== (c.value ?? '') : c.op === 'empty' ? v === '' : v !== '';
};

/**
 * The fields of a config shown for these values. Chain-aware: a field hidden by its own condition
 * contributes no value to the conditions of others (a stale value on a hidden control cannot reveal
 * the fields that depend on it). Fixpoint, bounded by the field count.
 */
export function shownFieldNames(config: Record<string, FieldConfigEntry>, values: Record<string, unknown>, get: (v: Record<string, unknown>, k: string) => unknown = (v, k) => v[k]): Set<string> {
  const names = Object.keys(config).filter((k) => config[k]!.visibility !== 'hidden');
  let shown = new Set(names);
  for (let i = 0; i <= names.length; i++) {
    const next = new Set(names.filter((k) => {
      const when = config[k]!.visible_when;
      return !when || when.some((g) => g.every((c) => holds(c, shown.has(c.field) ? get(values, c.field) : undefined)));
    }));
    if (next.size === shown.size && [...next].every((k) => shown.has(k))) return next;
    shown = next;
  }
  return shown;
}

/** AutoForm `conditions` for a config's conditional fields (none when it has none). */
export function conditionsFor(config: Record<string, FieldConfigEntry> | undefined, get?: (v: Record<string, unknown>, k: string) => unknown): Record<string, (values: Record<string, unknown>) => boolean> | undefined {
  if (!config || !Object.values(config).some((e) => e.visible_when)) return undefined;
  const out: Record<string, (values: Record<string, unknown>) => boolean> = {};
  for (const k of Object.keys(config)) out[k] = (values) => shownFieldNames(config, values, get).has(k);
  return out;
}

const CATALOG_LIMIT = 200;
// Catalogs are configuration: the config tier (docs/architecture.md "Caching"); every write
// through the engine invalidates its resource, so an admin's edit shows at once.
const STALE = CACHE.config.staleTime;

function useCatalog<T>(resource: string, sort: string) {
  const q = useResourceList<T>(resource, { sort }, { limit: CATALOG_LIMIT, staleTime: STALE });
  const byId = useMemo(() => new Map(q.rows.map((r) => [(r as { id: string }).id, r])), [q.rows]);
  return { rows: q.rows, byId, isLoading: q.isLoading, error: q.error, refetch: q.refetch };
}

export const useStatuses = () => useCatalog<StatusRow>('ticket-statuses', 'sort');
export const usePriorities = () => useCatalog<PriorityRow>('ticket-priorities', 'sort');
export const useImpacts = () => useCatalog<LevelRow>('ticket-impacts', 'sort');
export const useUrgencies = () => useCatalog<LevelRow>('ticket-urgencies', 'sort');
export const useOutcomes = () => useCatalog<OutcomeRow>('action-outcomes', 'sort');
export const useQueues = () => useCatalog<QueueRow>('queues', 'sort');
export const useTicketTypes = () => useCatalog<TypeRow>('ticket-types', 'sort');
export const useTechnicians = () => useCatalog<TechnicianRow>('technicians', 'name');

/** Tone for a status category (chips, badges, board headers). */
export function statusTone(category: StatusRow['category'] | string | null | undefined): 'pos' | 'neg' | 'warn' | 'info' | 'accent' | 'muted' {
  switch (category) {
    case 'new': return 'info';
    case 'open': return 'accent';
    case 'pending': return 'warn';
    case 'resolved': return 'pos';
    case 'cancelled': return 'neg';
    default: return 'muted';
  }
}

export function priorityTone(level: number | null | undefined): 'neg' | 'warn' | 'info' | 'muted' {
  if (level == null) return 'muted';
  if (level <= 1) return 'neg';
  if (level === 2) return 'warn';
  if (level === 3) return 'info';
  return 'muted';
}
